import { getSocial } from '@/core/lib/api/main/landing';
import { SocialItem } from '@/core/types/landing';
import {
  Instagram,
  FacebookIcon,
  Twitter,
  Youtube,
  Linkedin,
} from 'lucide-react';
import { JSX, SVGProps, ComponentPropsWithoutRef, ReactNode } from 'react';

interface IconProps extends SVGProps<SVGSVGElement> {
  fill?: string;
  color?: string;
  className?: string;
}

type LucideIconProps = ComponentPropsWithoutRef<'svg'> & {
  color?: string;
  className?: string;
};

const DefaultIcon = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <div className={`${className} bg-gray-200 rounded-full`} aria-hidden="true" />
)
// کامپوننت‌های Lucide با prop color
const InstagramIcon = ({ color = '#171717', className = 'w-5 h-5', ...props }: LucideIconProps) => (
  <Instagram className={className} style={{ color }} {...props} />
);

const LinkedinIcon = ({ color = '#171717', className = 'w-5 h-5', ...props }: LucideIconProps) => (
  <Linkedin className={className} style={{ color }} {...props} />
);

const FacebookIconComp = ({ color = '#171717', className = 'w-5 h-5', ...props }: LucideIconProps) => (
  <FacebookIcon className={className} style={{ color }} {...props} />
);

const TwitterIcon = ({ color = '#171717', className = 'w-5 h-5', ...props }: LucideIconProps) => (
  <Twitter className={className} style={{ color }} {...props} />
);

const YoutubeIcon = ({ color = '#171717', className = 'w-5 h-5', ...props }: LucideIconProps) => (
  <Youtube className={className} style={{ color }} {...props} />
);

// SVG Icons با prop fill (و color برای سازگاری)
const AparatIcon = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={fill || color}
    className={className}
    viewBox="0 0 24 24"
    role="img"
    {...props}
  >
    <path d="M12.001 1.594c-9.27-.003-13.913 11.203-7.36 17.758a10.403 10.403 0 0 0 17.76-7.355c0-5.744-4.655-10.401-10.4-10.403zM6.11 6.783c.501-2.598 3.893-3.294 5.376-1.103 1.483 2.19-.422 5.082-3.02 4.582A2.97 2.97 0 0 1 6.11 6.783zm4.322 8.988c-.504 2.597-3.897 3.288-5.377 1.096-1.48-2.192.427-5.08 3.025-4.579a2.97 2.97 0 0 1 2.352 3.483zm1.26-2.405c-1.152-.223-1.462-1.727-.491-2.387.97-.66 2.256.18 2.04 1.334a1.32 1.32 0 0 1-1.548 1.053zm6.198 3.838c-.501 2.598-3.893 3.293-5.376 1.103-1.484-2.191.421-5.082 3.02-4.583a2.97 2.97 0 0 1 2.356 3.48zm-1.967-5.502c-2.598-.501-3.293-3.896-1.102-5.38 2.19-1.483 5.081.422 4.582 3.02a2.97 2.97 0 0 1-3.48 2.36zM13.59 23.264l2.264.61a3.715 3.715 0 0 0 4.543-2.636l.64-2.402a11.383 11.383 0 0 1-7.448 4.428zm7.643-19.665L18.87 2.97a11.376 11.376 0 0 1 4.354 7.62l.65-2.459A3.715 3.715 0 0 0 21.231 3.6zM.672 13.809l-.541 2.04a3.715 3.715 0 0 0 2.636 4.543l2.107.562a11.38 11.38 0 0 1-4.203-7.145zM10.357.702 8.15.126a3.715 3.715 0 0 0-4.547 2.637l-.551 2.082A11.376 11.376 0 0 1 10.358.702z" />
  </svg>
);

const BaleIcon = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={fill || color}
    className={className}
    viewBox="0 0 24 24"
    role="img"
    {...props}
  >
    <path d="M11.425 23.987a12.218 12.218 0 0 1-2.95-.514 6.578 6.578 0 0 0-.336-.116C4.936 22.303 2.22 19.763.913 16.599a11.92 11.92 0 0 1-.9-4.063C.005 12.377.001 10.246 0 6.74 0 .71-.005 1.137.07.903.23.394.673.05 1.224.005c.421-.034.7.088 1.603.699.562.38 1.119.78 1.796 1.289.315.237.353.261.376.247l.35-.23c.58-.381 1.11-.677 1.7-.945A11.913 11.913 0 0 1 9.766.21a11.19 11.19 0 0 1 2.041-.2c1.14-.016 2.077.091 3.152.36 3.55.888 6.538 3.411 8.028 6.78.492 1.113.845 2.43.945 3.522.033.366.039.43.053.611.008.105.015.406.015.669 0 .783-.065 1.57-.169 2.064a5.474 5.474 0 0 0-.046.26c-.056.378-.214.987-.399 1.535-.205.613-.367.999-.684 1.633a11.95 11.95 0 0 1-2.623 3.436c-.44.396-.829.705-1.26 1.003-.647.445-1.307.812-2.039 1.134-.6.265-1.44.539-2.101.686a11.165 11.165 0 0 1-1.178.202 12.28 12.28 0 0 1-2.076.082zm-.61-5.92c.294-.06.678-.209.864-.337.144-.099.428-.376 2.064-2.013a161.8 161.8 0 0 1 1.764-1.753c.017 0 1.687-1.67 1.687-1.689 0-.02 1.64-1.648 1.661-1.648.01 0 .063-.047.118-.106.467-.495.682-.957.716-1.547.026-.433-.06-.909-.217-1.196a2.552 2.552 0 0 0-.983-1.024c-.281-.163-.512-.233-.888-.27-.306-.031-.688 0-.948.075-.243.07-.603.274-.853.481-.042.035-1.279 1.265-2.748 2.733l-2.671 2.67-1.093-1.09c-.6-.6-1.12-1.114-1.155-1.142a2.419 2.419 0 0 0-1.338-.51c-.404-.013-.91.09-1.224.25a2.89 2.89 0 0 0-.659.526c-.108.12-.287.357-.29.385-.003.03-.009.044-.065.16a2.312 2.312 0 0 0-.224.91c-.011.229-.01.265.019.491.045.353.24.781.51 1.115.05.063.97.992 2.044 2.064 1.507 1.505 1.98 1.97 2.074 2.039.327.24.683.388 1.101.456.182.03.5.016.734-.03z" />
  </svg>
);

const EitaaIcon = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={fill || color}
    className={className}
    viewBox="0 0 24 24"
    role="img"
    {...props}
  >
    <path d="M5.968 23.942a6.624 6.624 0 0 1-2.332-.83c-1.62-.929-2.829-2.593-3.217-4.426-.151-.717-.17-1.623-.15-7.207C.288 5.47.274 5.78.56 4.79c.142-.493.537-1.34.823-1.767C2.438 1.453 3.99.445 5.913.08c.384-.073.94-.08 6.056-.08 6.251 0 6.045-.009 7.066.314a6.807 6.807 0 0 1 4.314 4.184c.33.937.346 1.087.369 3.555l.02 2.23-.391.268c-.558.381-1.29 1.06-2.316 2.15-1.182 1.256-2.376 2.42-2.982 2.907-1.309 1.051-2.508 1.651-3.726 1.864-.634.11-1.682.067-2.302-.095-.553-.144-.517-.168-.726.464a6.355 6.355 0 0 0-.318 1.546l-.031.407-.146-.03c-1.215-.241-2.419-1.285-2.884-2.5a3.583 3.583 0 0 1-.26-1.219l-.016-.34-.309-.284c-.644-.59-1.063-1.312-1.195-2.061-.212-1.193.34-2.542 1.538-3.756 1.264-1.283 3.127-2.29 4.953-2.68.658-.14 1.818-.177 2.403-.075 1.138.198 2.067.773 2.645 1.639.182.271.195.31.177.555a.812.812 0 0 1-.183.493c-.465.651-1.848 1.348-3.336 1.68-2.625.585-4.294-.142-4.033-1.759.026-.163.04-.304.031-.313-.032-.032-.293.104-.575.3-.479.334-.903.984-1.05 1.607-.036.156-.05.406-.034.65.02.331.053.454.192.736.092.186.275.45.408.589l.24.251-.096.122a4.845 4.845 0 0 0-.677 1.217 3.635 3.635 0 0 0-.105 1.815c.103.461.421 1.095.739 1.468.242.285.797.764.886.764.024 0 .044-.048.044-.106.001-.23.184-.973.326-1.327.423-1.058 1.351-1.96 2.82-2.74.245-.13.952-.47 1.572-.757 1.36-.63 2.103-1.015 2.511-1.305 1.176-.833 1.903-2.065 2.14-3.625.086-.57.086-1.634 0-2.207-.368-2.438-2.195-4.096-4.818-4.37-2.925-.307-6.648 1.953-8.942 5.427-1.116 1.69-1.87 3.565-2.187 5.443-.123.728-.169 2.08-.093 2.75.193 1.704.822 3.078 1.903 4.156a6.531 6.531 0 0 0 1.87 1.313c2.368 1.13 4.99 1.155 7.295.071.996-.469 1.974-1.196 3.023-2.25 1.02-1.025 1.71-1.88 3.592-4.458 1.04-1.423 1.864-2.368 2.272-2.605l.15-.086-.019 3.091c-.018 2.993-.022 3.107-.123 3.561-.6 2.678-2.54 4.636-5.195 5.242l-.468.107-5.775.01c-4.734.008-5.85-.002-6.19-.056z" />
  </svg>
);

const RubikaIcon = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    id="Layer_1"
    className={className}
    data-name="Layer 1"
    viewBox="0 0 495.37 543.04"
    {...props}
  >
    <path
      d="M246.07,0h1.76c.48,42.73-.25,128.44-.25,128.44-40.86-22.92-82-45.3-122.84-68.29q40.13-22.81,80.32-45.51c12.69-7.16,26.33-13.31,41-14.64Z"
      fill={fill || color}
    />
    <path
      d="M247.73,0h3C266.62,1.43,281,9,294.67,16.68Q332.91,38.25,371.05,60c-41,22.73-82.45,45.82-123.51,68.48-.35-42.73.63-85.77.15-128.5Z"
      fill={fill || color}
    />
    <path
      d="M124.77,60.11,247.61,128.4,123.92,200.66s-.06-45.85.08-68.71C123.89,108.21,124.77,60.11,124.77,60.11Z"
      fill={fill || color}
    />
    <path
      d="M371.19,60l.35.19c-.1,42.59,0,85.18,0,127.77-.07,4.17-.11,12.77-.11,12.77L247.62,128.45S330.2,82.76,371.23,60Z"
      fill={fill || color}
    />
    <path
      d="M371.44,60.21c27.28,15.51,54.37,31.36,81.47,47.17,13.11,7.36,27,15.52,34.16,29.39-38.38,21.25-115.68,64-115.68,64s-.06-8.61,0-12.78q0-63.89,0-127.77Z"
      fill={fill || color}
    />
    <path
      d="M46.5,105c25.65-15,78.19-44.88,78.19-44.88L124,200.66S47,158.21,8.71,137C17.05,122.13,32.11,113.12,46.5,105Z"
      fill={fill || color}
    />
    <path
      d="M247.5,128.44l123.81,72.3s-39.84,22-59.62,32.64c-21.14,12-64.09,35.82-64.09,35.82L123.94,200.66Z"
      fill={fill || color}
    />
    <path
      d="M0,193C.11,174.17-.23,154.15,8.66,137,47,158.21,123.92,200.66,123.92,200.66,82.55,224.58,41.38,248.86,0,272.81,0,246.21,0,219.62,0,193Z"
      fill={fill || color}
    />
    <path
      d="M487.07,136.77c7.54,14.1,8.64,30.5,8.22,46.19v90.11L371.41,200.76S448.69,158,487.07,136.77Z"
      fill={fill || color}
    />
    <path
      d="M123.92,200.66c.19-.08,123.66,68.54,123.66,68.54s0,71.69.07,106.76c.2,11.29-.07,34.11-.07,34.11-41.27-22.78-82.32-45.76-123.6-68.54l-.07-.31V200.66Z"
      fill={fill || color}
    />
    <path
      d="M0,272.81c41.35-24,82.52-48.23,123.89-72.15q-.06,70.29,0,140.56c-6.35-2.65-12.13-6.43-18.19-9.64C70.5,312.21,35.38,292.39,0,273.21v-.4Z"
      fill={fill || color}
    />
    <path
      d="M371.39,200.76v86.18c-.13,18.27.46,36.47,0,54.73-41.21,22.4-123.82,68.4-123.82,68.4s.27-22.82.07-34.11c0-35.07-.07-106.76-.07-106.76Z"
      fill={fill || color}
    />
    <path
      d="M371.39,200.76s123.88,72.14,123.88,72.31c-41.17,22.82-123.87,68.6-123.87,68.6h0c.46-18.26-.13-36.46,0-54.73V200.76Z"
      fill={fill || color}
    />
    <path
      d="M0,273.21c35.36,19.21,70.43,39,105.7,58.4,6.06,3.21,11.84,7,18.19,9.64l.07.31Q67,374.79,10,408.05C6,402,4,394.86,2.53,387.8.14,376,0,364,0,352v-78.8Z"
      fill={fill || color}
    />
    <path
      d="M495.27,273.07c.06,27.65,0,55.3,0,82.95.4,17.73-.12,36.62-9.57,52.25-34.2-20-114.33-66.6-114.33-66.6S454.1,295.89,495.27,273.07Z"
      fill={fill || color}
    />
    <path
      d="M10,408Q67,374.72,124,341.53c-.25,46.83.08,93.67-.16,140.49q-40.35-22.75-80.49-45.86C30.82,428.76,18,420.53,10,408Z"
      fill={fill || color}
    />
    <path
      d="M124,341.53c41.28,22.78,82.33,45.76,123.6,68.54-41.2,24-123.7,72-123.76,71.95.24-46.82-.09-93.66.16-140.49Z"
      fill={fill || color}
    />
    <path
      d="M371.2,341.76l.2-.09c.12,46.84-.25,93.69.19,140.53l-.31.15c-32.66-19.51-65.71-38.4-98.52-57.68-8.26-4.7-25.18-14.6-25.18-14.6S330,364.21,371.2,341.76Z"
      fill={fill || color}
    />
    <path
      d="M371.4,341.67h0c3.65,2.49,80.13,46.62,114.33,66.6-4.86,8.3-12.56,14.39-20.32,19.84-12.52,8.42-25.88,15.47-38.9,23.05-18.31,10.35-36.5,20.91-54.92,31-.44-46.84-.07-93.69-.19-140.53Z"
      fill={fill || color}
    />
    <path
      d="M247.58,410.07V543c-10.25-1.51-21.42-3.87-30.5-9.08C186,516.66,154.81,499.5,123.82,482,165,458,206.38,434.12,247.58,410.07Z"
      fill={fill || color}
    />
    <path
      d="M247.58,410.07s16.92,9.9,25.18,14.6c32.81,19.28,65.86,38.17,98.52,57.68q-47.79,26.44-95.57,53A62.47,62.47,0 0 1,249.84,543h-2.26Z"
      fill={fill || color}
    />
    <path d="M602.83,233.66" fill={fill || color} />
  </svg>
);

const SoroushPlusIcon = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={fill || color}
    className={className}
    viewBox="0 0 977.48 1000"
    {...props}
  >
    <g id="File">
      <g>
        <path
          d="M781.85,1000H768.5l-5.16-2.68c-19.45-10.12-38.68-20.64-57.91-31.17-13.83-7.58-27.68-15.15-41.59-22.57-84.85,32.86-180.36,41.17-270.3,23.33A487.32,487.32,0 0 1,5.3,564C-.25,529.33-1.86,491.78.51,452.43c2.51-27.77,5.6-48.52,10.09-68a445.54,445.54,0 0 1,24.82-80.1,441.92,441.92,0 0 1,36.93-72.61C91,201,114.82,170.86,143,142.44c15.14-14.74,27-25.29,38.92-34.5C253.8,49.46,344.81,12,437.74,2.72l3.36-.31c5.7-.54,11.42-1,17.07-2L462.32,0l56.07.24c7,1,14.11,1.82,21.18,2.63,15.68,1.82,31.9,3.69,48.25,7.54a429.23,429.23,0 0 1,68.11,19.38c21.59,7.39,44.44,17.8,71.21,32.49A491.07,491.07,0 0 1,868.22,181.09a491.73,491.73,0 0 1,57,88.35,455.74,455.74,0 0 1,31.62,80.21,457.7,457.7,0 0 1,17.07,84.17A482.63,482.63,0 0 1,971.22,562a466.52,466.52,0 0 1-33.56,117.85c-28,66.13-71.79,126.49-127,175,0,5.27.1,10.53.16,15.81.34,32.52.69,66.14-1,99.44l-.23,4.75-2.05,4.29a90.5,90.5,0 0 1-5,8.76l-7,11.18ZM666.39,891.1l9.9,5.2c17.44,9.15,34.72,18.61,52,28.08,11.59,6.34,23.2,12.7,34.84,19,.55-24.06.29-48.39.05-72.12-.09-9-.19-18-.24-27L762.88,833l8.56-7.19c53.53-44.94,95.87-101.9,122.45-164.7A420.54,420.54,0 0 0,924.13,555l0-.25A435.82,435.82,0 0 0,926.57,439a412.2,412.2,0 0 0-15.4-75.85,410.25,410.25,0 0 0-28.5-72.33A441.95,441.95,0 0 0,704,103.91c-24.12-13.23-44.6-22.59-64.24-29.33a388.73,388.73,0 0 0-62-17.64c-14.49-3.39-28.66-5-43.66-6.76-6.91-.8-13.83-1.61-20.73-2.56h-49c-6.48,1.05-12.73,1.63-18.79,2.2l-3.16.29c-83.66,8.34-165.68,42.12-230.86,95.11-10.83,8.42-21.49,17.88-35,31-25.24,25.47-46.76,52.66-63.66,80.52A397.1,397.1,0 0 0,79.5,322.34A400.24,400.24,0 0 0,57.1,394.7C53,412.42,50.24,431.2,48,456c-2.11,35.17-.67,69.22,4.34,100.52A440.86,440.86,0 0 0,402.69,920.18c84.63,16.76,174.58,8,253.39-24.79Z"
          transform="translate(0.55)"
        />
        <path
          d="M430.51,806c-64.32,0-128.64-20.89-180.14-60.46l-3-2.5c-19.35-16.3-41.29-34.76-55.39-60l-36.62-65.56L223.07,650c10.88,5,81.14,24.5,128.92-10.23a108.76,108.76,0 0 0,37.89-62.5c4.3-20.72,2.32-44.37-6-72.55-2.73-10.15-5.72-19.63-8.71-29.1-5.1-16.22-10.38-33-13.83-50.64q-7.39-43.9.49-84a219.06,219.06,0 0 1,47.24-97.34c51.76-63.77,169-70.72,174-71l49.28-2.63-28.61,40.21c-17,24-32.24,58.64-27.69,71.8,7.24,21.84,20.81,41.6,33.14,58.09,6.42,9.1,12.7,17.44,19,25.78,10.23,13.6,20.81,27.65,30.35,42.68,15.5,26.21,26.5,50.53,34,75,10.6,37.8,13.68,73.74,9.35,107.4a224.33,224.33,0 0 1-26.19,80.19c-33,56.07-90.12,100-157.07,121.17A287.07,287.07,0 0 1,430.51,806ZM279.14,707.59l1.08.91c62.29,47.83,148.4,62.81,223.94,38.49,56-17.71,103.39-53.9,130.14-99.33A176.38,176.38,0 0 0,654.65,585c3.53-27.39.9-57-7.79-88-6.25-20.25-15.78-41.23-29-63.54-8.11-12.77-17.67-25.47-27.78-38.91-6.6-8.75-13.18-17.51-19.48-26.46-14.21-19-30.42-42.75-39.71-70.79-7.55-21.86-.71-48.24,8.92-70.37-33.54,7.41-73.57,21.67-93.9,46.88l-.42.5a171.07,171.07,0 0 0-37,76.09c-4.05,20.74-4.18,43-.31,66,2.82,14.41,7.47,29.2,12.4,44.87,3.19,10.11,6.36,20.23,9.12,30.47,10.44,35.33,12.72,66.56,6.82,94.93-6.89,35-27.21,68.19-55.71,90.92C348.52,701.11,311.52,708.08,279.14,707.59Z"
          transform="translate(0.55)"
        />
      </g>
    </g>
  </svg>
);

const TelegramIcon = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={fill || color}
    className={className}
    viewBox="0 0 32 32"
    version="1.1"
    {...props}
  >
    <path d="M22.122 10.040c0.006-0 0.014-0 0.022-0 0.209 0 0.403 0.065 0.562 0.177l-0.003-0.002c0.116 0.101 0.194 0.243 0.213 0.403l0 0.003c0.020 0.122 0.031 0.262 0.031 0.405 0 0.065-0.002 0.129-0.007 0.193l0-0.009c-0.225 2.369-1.201 8.114-1.697 10.766-0.21 1.123-0.623 1.499-1.023 1.535-0.869 0.081-1.529-0.574-2.371-1.126-1.318-0.865-2.063-1.403-3.342-2.246-1.479-0.973-0.52-1.51 0.322-2.384 0.221-0.23 4.052-3.715 4.127-4.031 0.004-0.019 0.006-0.040 0.006-0.062 0-0.078-0.029-0.149-0.076-0.203l0 0c-0.052-0.034-0.117-0.053-0.185-0.053-0.045 0-0.088 0.009-0.128 0.024l0.002-0.001q-0.198 0.045-6.316 4.174c-0.445 0.351-1.007 0.573-1.619 0.599l-0.006 0c-0.867-0.105-1.654-0.298-2.401-0.573l0.074 0.024c-0.938-0.306-1.683-0.467-1.619-0.985q0.051-0.404 1.114-0.827 6.548-2.853 8.733-3.761c1.607-0.853 3.47-1.555 5.429-2.010l0.157-0.031zM15.93 1.025c-8.302 0.020-15.025 6.755-15.025 15.060 0 8.317 6.742 15.060 15.060 15.060s15.060-6.742 15.060-15.060c0-8.305-6.723-15.040-15.023-15.060h-0.002q-0.035-0-0.070 0z" />
  </svg>
);

const Pinterest = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    className={className}
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.5 12C3.5 7.30558 7.30558 3.5 12 3.5C16.6944 3.5 20.5 7.30558 20.5 12C20.5 16.6944 16.6944 20.5 12 20.5C10.9716 20.5 9.98587 20.3174 9.07345 19.9828C9.64822 18.4359 10.2115 16.8847 10.7517 15.3255C11.326 15.7348 12.0668 16 13 16C14.935 16 16.9749 14.7247 17.4806 12.1961C18.1155 9.02148 15.5728 6 12 6C10.4972 6 9.01887 6.6037 7.91298 7.56243C6.80483 8.52311 6 9.90687 6 11.5C6 12.2746 6.23394 13.1378 6.79149 13.7057C7.17707 14.0919 7.82087 14.0933 8.20711 13.7071C8.59019 13.324 8.59749 12.7074 8.22899 12.3155C7.44315 11.3348 8.47852 9.71907 9.22306 9.07361C9.99585 8.40366 11.0175 8 12 8C14.4272 8 15.8845 9.97852 15.5194 11.8039C15.2165 13.3183 14.065 14 13 14C12.1821 14 11.7416 13.6547 11.4599 13.208C11.6137 12.7237 11.7454 12.2838 11.8387 11.9263C12.0311 11.1886 12.1473 10.3002 11.4839 9.7474C10.9908 9.33644 10.4087 9.42759 10.0528 9.60557C9.39135 9.93629 9 10.7099 9 11.5C9 11.9414 9.06873 12.6253 9.31675 13.3315C8.67824 15.258 7.98579 17.167 7.27924 19.0696C5.00045 17.5449 3.5 14.9477 3.5 12ZM12 1.5C6.20101 1.5 1.5 6.20101 1.5 12C1.5 17.799 6.20101 22.5 12 22.5C17.799 22.5 22.5 17.799 22.5 12C22.5 6.20101 17.799 1.5 12 1.5Z"
      fill={fill || color}
    />
  </svg>
);

const Tiktok = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill={fill || color}
    className={className}
    viewBox="0 0 32 32"
    version="1.1"
    {...props}
  >
    <path d="M16.656 1.029c1.637-0.025 3.262-0.012 4.886-0.025 0.054 2.031 0.878 3.859 2.189 5.213l-0.002-0.002c1.411 1.271 3.247 2.095 5.271 2.235l0.028 0.002v5.036c-1.912-0.048-3.71-0.489-5.331-1.247l0.082 0.034c-0.784-0.377-1.447-0.764-2.077-1.196l0.052 0.034c-0.012 3.649 0.012 7.298-0.025 10.934-0.103 1.853-0.719 3.543-1.707 4.954l0.020-0.031c-1.652 2.366-4.328 3.919-7.371 4.011l-0.014 0c-0.123 0.006-0.268 0.009-0.414 0.009-1.73 0-3.347-0.482-4.725-1.319l0.040 0.023c-2.508-1.509-4.238-4.091-4.558-7.094l-0.004-0.041c-0.025-0.625-0.037-1.25-0.012-1.862 0.49-4.779 4.494-8.476 9.361-8.476 0.547 0 1.083 0.047 1.604 0.136l-0.056-0.008c0.025 1.849-0.050 3.699-0.050 5.548-0.423-0.153-0.911-0.242-1.42-0.242-1.868 0-3.457 1.194-4.045 2.861l-0.009 0.030c-0.133 0.427-0.21 0.918-0.21 1.426 0 0.206 0.013 0.41 0.037 0.61l-0.002-0.024c0.332 2.046 2.086 3.59 4.201 3.59 0.061 0 0.121-0.001 0.181-0.004l-0.009 0c1.463-0.044 2.733-0.831 3.451-1.994l0.010-0.018c0.267-0.372 0.45-0.822 0.511-1.311l0.001-0.014c0.125-2.237 0.075-4.461 0.087-6.698 0.012-5.036-0.012-10.060 0.025-15.083z" />
  </svg>
);

const Whatsapp = ({ fill = '#171717', color, className = 'w-5 h-5', ...props }: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M6.014 8.00613C6.12827 7.1024 7.30277 5.87414 8.23488 6.01043L8.23339 6.00894C9.14051 6.18132 9.85859 7.74261 10.2635 8.44465C10.5504 8.95402 10.3641 9.4701 10.0965 9.68787C9.7355 9.97883 9.17099 10.3803 9.28943 10.7834C9.5 11.5 12 14 13.2296 14.7107C13.695 14.9797 14.0325 14.2702 14.3207 13.9067C14.5301 13.6271 15.0466 13.46 15.5548 13.736C16.3138 14.178 17.0288 14.6917 17.69 15.27C18.0202 15.546 18.0977 15.9539 17.8689 16.385C17.4659 17.1443 16.3003 18.1456 15.4542 17.9421C13.9764 17.5868 8 15.27 6.08033 8.55801C5.97237 8.24048 5.99955 8.12044 6.014 8.00613Z"
      fill={fill || color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 23C10.7764 23 10.0994 22.8687 9 22.5L6.89443 23.5528C5.56462 24.2177 4 23.2507 4 21.7639V19.5C1.84655 17.492 1 15.1767 1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23ZM6 18.6303L5.36395 18.0372C3.69087 16.4772 3 14.7331 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C11.0143 21 10.552 20.911 9.63595 20.6038L8.84847 20.3397L6 21.7639V18.6303Z"
      fill={fill || color}
    />
  </svg>
);


// iconMap حالا Record<string, React.ComponentType<IconProps>> هست
import { ComponentType } from 'react';

const iconMap: Record<string, ComponentType<IconProps>> = {
  instagram: InstagramIcon,
  linkedin: LinkedinIcon,
  facebook: FacebookIconComp,
  x: TwitterIcon,
  youtube: YoutubeIcon,
  telegram: TelegramIcon,
  pinterest: Pinterest,
  tiktok: Tiktok,
  whatsapp: Whatsapp,
  aparat: AparatIcon,
  bale: BaleIcon,
  eitaa: EitaaIcon,
  rubika: RubikaIcon,
  soroush_plus: SoroushPlusIcon,
};

interface SocialMediaProps {
  iconColor?: string;
}

export default async function SocialMedia({ iconColor = '#171717' }: SocialMediaProps) {
  let socialItems: { data: SocialItem[] } | null = null;


  try {
    socialItems = await getSocial();

  } catch (error) {
    console.error('Error fetching social links:', error);
  }

  if (!socialItems || socialItems.data.length === 0) {
    return null;
  }

  const items = socialItems.data;

  return (
    <div className="flex gap-4 items-center flex-wrap">
      {items.map((item) => {
        const IconComponent = iconMap[item.type] ?? DefaultIcon;

        return (
          <a
            key={item.id}
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            title={item.title || item.type}
            className="text-primary-400 hover:text-primary-600 transition-colors"
          >
            <IconComponent color={iconColor} className="w-5 h-5" />
          </a>
        );
      })}
    </div>
  );
}